import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "gorton-cookie-consent";
// Note: storing the consent choice in localStorage is the standard pattern for
// cookie-consent banners (the value is a non-sensitive user preference like
// "accept" / "reject"). It is not authentication or PII, so httpOnly cookies
// are not required here.

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay slightly so the banner doesn't flash on first render
    const t = setTimeout(() => {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    }, 500);
    return () => clearTimeout(t);
  }, []);

  const record = (choice) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, ts: new Date().toISOString() })
    );
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[60] p-4 sm:p-6 animate-in slide-in-from-bottom duration-300"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="max-w-5xl mx-auto bg-white border border-slate-200 shadow-2xl rounded-2xl p-5 sm:p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Cookie className="h-5 w-5 text-emerald-700" />
          </div>
          <div className="text-sm text-slate-700 leading-relaxed">
            <p className="font-semibold text-slate-900 mb-1">We value your privacy</p>
            <p>
              We use essential cookies to make the site work and optional analytics
              cookies to understand how visitors use it. See our{" "}
              <Link
                to="/privacy-policy"
                className="text-emerald-600 hover:underline font-medium"
                data-testid="cookie-privacy-link"
              >
                Privacy Policy
              </Link>{" "}
              for details.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => record("reject")}
            className="flex-1 md:flex-none border-slate-300 hover:bg-slate-50"
            data-testid="cookie-reject-btn"
          >
            Reject Non-Essential
          </Button>
          <Button
            size="sm"
            onClick={() => record("accept")}
            className="flex-1 md:flex-none bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="cookie-accept-btn"
          >
            Accept All
          </Button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => record("dismiss")}
            className="hidden md:inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            data-testid="cookie-dismiss-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
